// The parts of the site's (steamdata.dev's) contract the API needs too: the
// words a search is read as, which both spell one way, and the site's page
// URLs, which the API's sitemap lists. Here rather than in the site's page
// modules (../site/public/), so the API bundles
// and type-checks only these.

import { AROUND_THE_CLOCK, REGIONS } from "./evenings.ts";

// A query's words, as GET /api/search (workers/web) reads them: runs of
// letters, digits and marks, lowercased, with apostrophes dropped as
// tracked_apps.search_name drops them. Words past the eighth are ignored, and
// each is capped, so a pasted essay can't make an expensive MATCH. app.js
// asks for the words rather than the query as typed, so every spelling of
// the same search is one API cache entry.
export function searchWords(q: string): string[] {
  return (
    q
      .normalize("NFKC")
      .toLowerCase()
      .replace(/['’]/g, "")
      .match(/[\p{L}\p{M}\p{N}]+/gu) ?? []
  )
    .slice(0, 8)
    .map((word) => word.slice(0, 40));
}

// A game's page.
export const appPath = (appId: string | number): string => `/apps/${appId}`;

// The tags index (list-pages.ts's tagsPage).
export const TAGS_PATH = "/tags";

// A tag page's path.
export const tagPagePath = (tag: string, value: string): string => `/tags/${tag}/${value}`;

// The site's tag pages: each tag with pages, and its values with one, in the
// order the site lists them. tag-pages.ts's TAG_PAGES says what each page says,
// for exactly these (its type and build.ts check).
export const TAG_PAGE_VALUES = {
  trend: ["rising", "falling"],
  rank: ["climbing", "dropping"],
  peak_share: ["near", "far"],
  update_bump: ["up"],
  sale_bump: ["up"],
  on_sale: ["now"],
  new_release: ["recent"],
  // Pure, as is TAG_PAGE_LIST, so a bundle that only needs a path helper
  // (the embed card's appPath) drops them.
  busy_hours: /* @__PURE__ */ (REGIONS as readonly string[]).concat(AROUND_THE_CLOCK)
} satisfies Record<string, readonly string[]>;

// A tag with pages: a key of TAG_PAGE_VALUES.
export type TagPageName = keyof typeof TAG_PAGE_VALUES;

// Every tag page, as { tag, value, path }, in order: for the sitemap, and
// for the tags index's cards.
// Object.entries types its keys as string; they are TAG_PAGE_VALUES's.
export const TAG_PAGE_LIST: { tag: TagPageName; value: string; path: string }[] = /* @__PURE__ */ Object.entries(
  TAG_PAGE_VALUES
).flatMap(([tag, values]) =>
  values.map((value) => ({ tag: tag as TagPageName, value, path: tagPagePath(tag, value) }))
);

// The tag pages page 0 leads with, as cards of their top games, in this
// order: GET /api/movers serves each one's list (by ?state=, so each must be
// a state) in one request. A card with `keepTop` shows its list's top games
// whatever earlier cards showed, as its order means something of its own (the
// biggest discounts); the others prefer games no earlier card showed.
export const HOME_TAGS: { tag: TagPageName; value: string; keepTop?: true }[] = [
  { tag: "trend", value: "rising" },
  { tag: "trend", value: "falling" },
  { tag: "rank", value: "climbing" },
  { tag: "peak_share", value: "near" },
  { tag: "on_sale", value: "now", keepTop: true }
];

// A company's page: the games it developed (/developer/<slug>) or published
// (/publisher/<slug>), by companySlug's slug (companies.ts), which the API
// finds them by.
export const companyPath = (role: string, slug: string): string =>
  `/${role}/${encodeURIComponent(slug)}`;

// The fewest games a company page needs to be indexed and in the sitemap.
// A page with fewer is a short list of rows each game's own page says
// better, and Google left most of them unindexed as thin.
export const COMPANY_MIN_GAMES = 4;
